/**
 * Utility functions for document type detection
 */

/**
 * Determines the document type from a given document object or filename
 * @param doc Document object or filename string
 * @returns Standardized document type string
 */
export const detectDocumentType = (doc: any): string => {
  // Handle direct string input (filename) or object with original_filename
  const fileName = typeof doc === 'string'
    ? doc.toLowerCase()
    : (doc?.original_filename || '').toLowerCase();

  // Handle document_type or category fields if they exist
  const docType = typeof doc === 'object' ?
    (doc?.document_type?.toLowerCase() || doc?.category?.toLowerCase() || '') : '';

  // If no filename and no document_type, return Other
  if (!fileName && !docType) return 'Other Documents';

  // Check document_type or category fields first if they explicitly match our types
  if (docType) {
    if (docType.includes('adr') || docType === 'adr metrics') return 'ADR Metrics';
    if (docType.includes('revpar') || docType === 'revpar metrics') return 'RevPAR Metrics';
    if (docType.includes('occup') || docType === 'occupancy metrics') return 'Occupancy Metrics';
    if (docType.includes('str') || docType === 'str report') return 'STR Report';
    if (docType === 'profit & loss statement' || docType.includes('p&l')) return 'Profit & Loss Statement';
    if (docType === 'financial documents' || docType.includes('financial')) return 'Financial Documents';
  }

  // ADR detection - check for various forms
  if (fileName.includes('adr') ||
      fileName.includes('average daily rate') ||
      fileName.includes('avg daily rate') ||
      fileName.includes('daily rate') ||
      fileName.includes('room rate')) {
    return 'ADR Metrics';
  }
  // RevPAR detection
  else if (fileName.includes('revpar') ||
          fileName.includes('rev par') ||
          fileName.includes('revenue per available room') ||
          fileName.includes('rev per available') ||
          fileName.includes('rev_par')) {
    return 'RevPAR Metrics';
  }
  // Occupancy detection
  else if (fileName.includes('occup') ||
          fileName.includes('occ rate') ||
          fileName.includes('occupancy rate') ||
          fileName.includes('occ_rate') ||
          fileName.includes('room occup')) {
    return 'Occupancy Metrics';
  }
  // STR Report detection
  else if (fileName.includes('str') ||
          fileName.includes('star') ||
          fileName.includes('smith travel') ||
          fileName.includes('str report') ||
          fileName.includes('competitive set')) {
    return 'STR Report';
  }
  // Financial documents detection - broader categories first
  else if (fileName.includes('financial') ||
          fileName.includes('finance') ||
          fileName.includes('balance sheet') ||
          fileName.includes('cashflow') ||
          fileName.includes('cash flow') ||
          fileName.includes('income statement') ||
          fileName.includes('annual report') ||
          fileName.includes('quarterly report') ||
          fileName.includes('budget') ||
          fileName.includes('forecast')) {
    return 'Financial Documents';
  }
  // P&L detection - this should be last as it has more common terms
  else if (fileName.includes('p&l') ||
          fileName.includes('profit') ||
          fileName.includes('loss') ||
          fileName.includes('p and l') ||
          fileName.includes('statement')) {
    return 'Profit & Loss Statement';
  }

  // Default
  return 'Other Documents';
};

/**
 * Sort function for document types to ensure consistent order
 */
export const sortDocumentTypes = (a: string, b: string): number => {
  const order = [
    'Financial Documents',
    'Profit & Loss Statement',
    'STR Report',
    'ADR Metrics',
    'RevPAR Metrics',
    'Occupancy Metrics',
    'Other Documents'
  ];

  const indexA = order.indexOf(a);
  const indexB = order.indexOf(b);

  if (indexA === -1 && indexB === -1) {
    return a.localeCompare(b); // alphabetical for any types not in the order list
  }

  if (indexA === -1) return 1; // unknown types go at the end
  if (indexB === -1) return -1;

  return indexA - indexB;
};

/**
 * List of standard document types
 */
export const STANDARD_DOCUMENT_TYPES = [
  'Financial Documents',
  'Profit & Loss Statement',
  'STR Report',
  'ADR Metrics',
  'RevPAR Metrics',
  'Occupancy Metrics',
  'Other Documents'
];